import React, { useEffect, useState, useCallback } from 'react';
import { FiActivity, FiSearch, FiFilter, FiTrash2, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import API from '../api/axios';
import { toast } from 'react-toastify';
import './ActivityLogs.css';

const ACTION_STYLE = {
  LOGIN:   { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', icon: '🔐' },
  LOGOUT:  { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0', icon: '🚪' },
  CREATE:  { bg: '#eff6ff', color: '#3b82f6', border: '#bfdbfe', icon: '➕' },
  UPDATE:  { bg: '#fffbeb', color: '#d97706', border: '#fde68a', icon: '✏️' },
  DELETE:  { bg: '#fff1f2', color: '#e11d48', border: '#fecdd3', icon: '🗑️' },
  VIEW:    { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe', icon: '👁️' },
};

const MODULES = ['Auth', 'Projects', 'Clients', 'Users', 'Roles', 'Modules', 'Attendance'];
const ACTIONS = ['LOGIN', 'LOGOUT', 'CREATE', 'UPDATE', 'DELETE'];

const timeAgo = (date) => {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const formatDateTime = (date) =>
  new Date(date).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 50 };
    if (filterUser) params.user = filterUser;
    if (filterModule) params.module = filterModule;
    if (filterAction) params.action = filterAction;
    if (filterFrom) params.from = filterFrom;
    if (filterTo) params.to = filterTo;

    API.get('/activity-logs', { params })
      .then(r => { setLogs(r.data.logs); setTotal(r.data.total); setPages(r.data.pages); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, filterUser, filterModule, filterAction, filterFrom, filterTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    API.get('/activity-logs/stats').then(r => setStats(r.data)).catch(() => {});
    API.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const clearFilters = () => {
    setFilterUser(''); setFilterModule(''); setFilterAction('');
    setFilterFrom(''); setFilterTo(''); setSearch(''); setPage(1);
  };

  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const handleClearAll = () => setShowClearConfirm(true);
  const doClearAll = async () => {
    setShowClearConfirm(false);
    try {
      await API.delete('/activity-logs/clear');
      toast.success('All logs cleared');
      fetchLogs();
      API.get('/activity-logs/stats').then(r => setStats(r.data)).catch(() => {});
    } catch { toast.error('Error clearing logs'); }
  };

  const filtered = logs.filter(l =>
    !search ||
    l.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
    l.description?.toLowerCase().includes(search.toLowerCase()) ||
    l.module?.toLowerCase().includes(search.toLowerCase())
  );

  const hasFilters = filterUser || filterModule || filterAction || filterFrom || filterTo;

  return (
    <Layout>
      {loading && <PageLoader text="Loading activity logs..." />}
      <div className="page">
        <div className="page-actions">
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-secondary" onClick={fetchLogs}><FiRefreshCw size={14} /> Refresh</button>
            <button className="btn-danger-sm" onClick={handleClearAll}><FiTrash2 size={14} /> Clear All</button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="al-stats">
            <div className="al-stat-card" style={{ borderTop: '3px solid #3b82f6' }}>
              <p className="al-stat-num" style={{ color: '#3b82f6' }}>{stats.total.toLocaleString()}</p>
              <p className="al-stat-label">Total Events</p>
            </div>
            <div className="al-stat-card" style={{ borderTop: '3px solid #16a34a' }}>
              <p className="al-stat-num" style={{ color: '#16a34a' }}>{stats.todayCount}</p>
              <p className="al-stat-label">Today</p>
            </div>
            {stats.byAction.slice(0, 2).map(a => {
              const st = ACTION_STYLE[a._id] || {};
              return (
                <div key={a._id} className="al-stat-card" style={{ borderTop: `3px solid ${st.color || '#94a3b8'}` }}>
                  <p className="al-stat-num" style={{ color: st.color }}>{a.count}</p>
                  <p className="al-stat-label">{a._id}</p>
                </div>
              );
            })}
            <div className="al-stat-card" style={{ borderTop: '3px solid #8b5cf6' }}>
              <p className="al-stat-num" style={{ color: '#8b5cf6' }}>{stats.byModule.length}</p>
              <p className="al-stat-label">Modules Active</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="al-filters">
          <div className="search-box" style={{ maxWidth: 260 }}>
            <FiSearch />
            <input placeholder="Search logs..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="al-select" value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1); }}>
            <option value="">All Users</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </select>
          <select className="al-select" value={filterModule} onChange={e => { setFilterModule(e.target.value); setPage(1); }}>
            <option value="">All Modules</option>
            {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="al-select" value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}>
            <option value="">All Actions</option>
            {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <div className="al-date-wrap">
            <FiCalendar size={13} />
            <input type="date" className="al-date-input" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(1); }} placeholder="From" />
          </div>
          <div className="al-date-wrap">
            <FiCalendar size={13} />
            <input type="date" className="al-date-input" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(1); }} placeholder="To" />
          </div>
          {hasFilters && (
            <button className="al-clear-btn" onClick={clearFilters}><FiFilter size={12} /> Clear</button>
          )}
          <span className="result-count" style={{ marginLeft: 'auto' }}>{total} result{total !== 1 ? 's' : ''}</span>
        </div>

        {/* Log table */}
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Action</th>
                  <th>Module</th>
                  <th>Description</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(log => {
                  const st = ACTION_STYLE[log.action] || ACTION_STYLE.VIEW;
                  return (
                    <tr key={log._id}>
                      <td>
                        <div className="user-cell">
                          <div className="al-avatar">{log.user?.name?.[0]?.toUpperCase() || '?'}</div>
                          <div>
                            <p className="user-cell-name">{log.user?.name || 'Unknown'}</p>
                            <p className="user-cell-sub">{log.user?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="al-action-badge" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                          {st.icon} {log.action}
                        </span>
                      </td>
                      <td>
                        <span className="al-module-badge">{log.module}</span>
                      </td>
                      <td className="al-description">{log.description || '—'}</td>
                      <td>
                        <p style={{ fontSize: '0.8rem', color: '#475569', margin: 0 }}>{formatDateTime(log.createdAt)}</p>
                        <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: 0 }}>{timeAgo(log.createdAt)}</p>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <FiActivity size={28} style={{ display: 'block', margin: '0 auto 0.5rem' }} />
                    No activity logs found.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="al-pagination">
            <button className="al-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span className="al-page-info">Page {page} of {pages}</span>
            <button className="al-page-btn" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
      {showClearConfirm && (
        <div className="modal-overlay" onClick={() => setShowClearConfirm(false)}>
          <div className="modal" style={{ maxWidth: 400, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>⚠️</div>
            <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Clear All Activity Logs</h3>
            <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>This will permanently delete all activity logs. This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowClearConfirm(false)}>Cancel</button>
              <button className="btn-danger" style={{ flex: 1 }} onClick={doClearAll}>Clear All Logs</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
